import React, { useState, useEffect } from 'react';
import { Table, Button } from 'react-bootstrap';
import { useLanguage } from '../context/LanguageContext';
import { useRowLock } from '../context/RowLockContext';
import { useAuth } from '../context/AuthContext';

interface Engagement {
  id: number;
  engagementCode: string;
  companyName: string;
}

interface CalendarEvent {
  engagementId: number;
  date: string;
  event: string;
}

const TimeSheet: React.FC = () => {
  // Sample engagements with engagement codes and company names
  const engagements: Engagement[] = [
    { id: 1, engagementCode: 'AUD20260001', companyName: 'ABC Company Limited' },
    { id: 2, engagementCode: 'AUD20260002', companyName: 'XYZ Corporation' },
    { id: 3, engagementCode: 'AUD20260003', companyName: 'DEF Industries' },
    { id: 4, engagementCode: 'ACC20260001', companyName: 'GHI Ltd' },
    { id: 5, engagementCode: 'GCS20260001', companyName: 'JKL Partners' }
  ];

  const { lockRow, unlockRow, isRowLocked, getLockOwner } = useRowLock();
  const { currentUser } = useAuth();

  // Generate dates starting from a Monday
  const generateDates = (startDate: Date, count: number): string[] => {
    const dates: string[] = [];
    const currentDate = new Date(startDate);

    // Find the Monday of the current week
    const dayOfWeek = currentDate.getDay();
    // Adjust to get Monday: if Sunday (0), go back 6 days; otherwise go back (dayOfWeek - 1) days
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(currentDate);
    monday.setDate(currentDate.getDate() + diffToMonday);

    for (let i = 0; i < count; i++) {
      const date = new Date(monday);
      date.setDate(monday.getDate() + i);
      dates.push(date.toISOString().split('T')[0]); // Format as YYYY-MM-DD
    }

    return dates;
  };

  // Calculate the start date for the current week based on the same reference as Calendar
  const getCurrentWeekMonday = (): Date => {
    // Use the same reference Monday as Calendar component (2025-12-29)
    const referenceMonday = new Date(Date.UTC(2025, 11, 29)); // December is month 11 (0-indexed)

    // Calculate the current week index based on the reference date
    const today = new Date();
    const timeDiff = today.getTime() - referenceMonday.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const weeksDiff = Math.floor(daysDiff / 7);

    // Calculate the Monday of the current week
    const currentWeekMonday = new Date(referenceMonday);
    currentWeekMonday.setDate(referenceMonday.getDate() + (weeksDiff * 7));
    return currentWeekMonday;
  };

  // Initial dates (7 days starting from Monday of current week)
  const [dates, setDates] = useState<string[]>(() => generateDates(getCurrentWeekMonday(), 7));

  // Track the current week offset (0 = current week, 1 = next week, -1 = previous week, etc.)
  const [weekOffset, setWeekOffset] = useState<number>(0);

  // Sample calendar events - all set to 'Y' for weekdays only
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);

  // State for editing
  const [editingCell, setEditingCell] = useState<{ engagementId: number; date: string } | null>(null);
  const [editValue, setEditValue] = useState('');

  // Navigate to previous week
  const goToPreviousWeek = () => {
    const newOffset = weekOffset - 1;
    setWeekOffset(newOffset);

    const currentMonday = getCurrentWeekMonday();
    const targetMonday = new Date(currentMonday);
    targetMonday.setDate(currentMonday.getDate() + (newOffset * 7));

    setDates(generateDates(targetMonday, 7));
  };

  // Navigate to next week
  const goToNextWeek = () => {
    const newOffset = weekOffset + 1;
    setWeekOffset(newOffset);

    const currentMonday = getCurrentWeekMonday();
    const targetMonday = new Date(currentMonday);
    targetMonday.setDate(currentMonday.getDate() + (newOffset * 7));

    setDates(generateDates(targetMonday, 7));
  };

  // Check if a date is weekend (Saturday or Sunday)
  const isWeekend = (dateString: string): boolean => {
    const date = new Date(dateString);
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // Sunday is 0, Saturday is 6
  };

  // Check if a date is New Year's Day (2026-01-01)
  const isNewYearsDay = (dateString: string): boolean => {
    return dateString === '2026-01-01';
  };

  // Check if a date is the special date (2025-12-29) that should have 'Y'
  const isSpecialDate = (dateString: string): boolean => {
    return dateString === '2025-12-29';
  };

  // Get event for a specific engagement and date
  const getEventForEngagementAndDate = (engagementId: number, date: string) => {
    // Return 'Y' only for the special date (2025-12-29), otherwise empty
    if (isSpecialDate(date)) {
      return 'Y';
    }
    // Check if there's a specific value in calendarEvents
    const event = calendarEvents.find(e => e.engagementId === engagementId && e.date === date);
    return event ? event.event : '';
  };

  // Handle cell click to start editing
  const handleCellClick = (engagementId: number, date: string) => {
    // Try to lock the row for editing
    if (lockRow('timeSheet', engagementId)) {
      setEditingCell({ engagementId, date });
      setEditValue(getEventForEngagementAndDate(engagementId, date));
    } else {
      // Row is locked by another user, show a message
      const lockOwner = getLockOwner('timeSheet', engagementId);
      alert(`${t('rowLockedBy')} ${lockOwner}`);
    }
  };

  // Save edited value
  const saveEdit = () => {
    if (editingCell) {
      const { engagementId, date } = editingCell;

      // Update the calendar events
      const updatedEvents = [...calendarEvents];
      const eventIndex = updatedEvents.findIndex(e => e.engagementId === engagementId && e.date === date);

      if (eventIndex !== -1) {
        updatedEvents[eventIndex] = { ...updatedEvents[eventIndex], event: editValue };
      } else {
        updatedEvents.push({ engagementId, date, event: editValue });
      }

      setCalendarEvents(updatedEvents);
      setEditingCell(null);
      setEditValue('');

      // Unlock the row after editing
      unlockRow('timeSheet', engagementId);
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditValue(e.target.value);
  };

  // Handle key press in input
  const handleInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      saveEdit();
    } else if (e.key === 'Escape') {
      setEditingCell(null);
      setEditValue('');
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };

  const { t } = useLanguage();

  return (
    <div className="timesheet">
      <h2>{t('timeSheet')}</h2>
      <p>{t('workCalendar')}</p>

      <div className="d-flex justify-content-between mb-3">
        <Button variant="primary" onClick={goToPreviousWeek}>
          &lt; {t('previousWeek')}
        </Button>
        <div>
          {dates[0]} {t('to')} {dates[dates.length - 1]}
        </div>
        <Button variant="primary" onClick={goToNextWeek}>
          {t('nextWeek')} &gt;
        </Button>
      </div>

      <Table responsive className="calendar-table table-fixed">
        <thead>
          <tr>
            <th className="engagement-code-cell">
              <div>{t('engagement')}</div>
              <div className="text-muted small">(項目代碼)</div>
            </th>
            <th className="company-name-cell">
              <div>{t('companyName')}</div>
              <div className="text-muted small">(公司名稱)</div>
            </th>
            {dates.map(date => {
              const dateObj = new Date(date);
              // Calculate day of week with Monday as 0: Monday=0, Tuesday=1, ..., Sunday=6
              const dayOfWeek = dateObj.getDay();
              const adjustedDay = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Sunday becomes 6, others shift down by 1
              const dayNames = [t('monday'), t('tuesday'), t('wednesday'), t('thursday'), t('friday'), t('saturday'), t('sunday')];
              const dayName = dayNames[adjustedDay];
              return (
                <th
                  key={date}
                  className={`calendar-header ${isWeekend(date) || isNewYearsDay(date) ? 'weekend' : ''}`}
                  style={{ backgroundColor: isWeekend(date) || isNewYearsDay(date) ? '#ffcccc' : 'inherit' }}
                >
                  {formatDate(date)}<br/>({dayName})
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {engagements.map(engagement => {
            const isLocked = isRowLocked('timeSheet', engagement.id);
            const lockOwner = isLocked ? getLockOwner('timeSheet', engagement.id) : null;
            const isCurrentUserEditing = editingCell?.engagementId === engagement.id;

            return (
              <tr
                key={engagement.id}
                style={{
                  border: isLocked
                    ? (isCurrentUserEditing ? '2px solid #008000' : '2px solid #ff0000')
                    : 'none',
                  position: 'relative'
                }}
              >
                {isLocked && !isCurrentUserEditing && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255, 0, 0, 0.1)', // Red for locked by another user
                      zIndex: 1,
                      pointerEvents: 'none',
                      textAlign: 'center',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      color: '#ff0000',
                      fontWeight: 'bold',
                      fontSize: '12px'
                    }}
                  >
                    {`${t('lockedBy')} ${lockOwner}`}
                  </div>
                )}
                <td
                  className="engagement-code-cell"
                  style={{
                    position: 'relative',
                    zIndex: 2,
                    borderBottom: 'none' // Remove bottom border
                  }}
                >
                  {engagement.engagementCode}
                </td>
                <td
                  className="company-name-cell"
                  style={{
                    position: 'relative',
                    zIndex: 2,
                    borderBottom: 'none' // Remove bottom border
                  }}
                >
                  {engagement.companyName}
                </td>
                {dates.map(date => {
                  const isEditing = editingCell?.engagementId === engagement.id && editingCell?.date === date;
                  const cellValue = getEventForEngagementAndDate(engagement.id, date);

                  return (
                    <td
                      key={`${engagement.id}-${date}`}
                      className={`calendar-cell ${isWeekend(date) || isNewYearsDay(date) ? 'weekend' : ''}`}
                      style={{
                        backgroundColor: isWeekend(date) || isNewYearsDay(date) ? '#ffcccc' : 'inherit',
                        height: '100px',
                        overflow: 'auto',
                        border: '1px solid #dee2e6',
                        verticalAlign: 'top',
                        cursor: isLocked && !isCurrentUserEditing ? 'not-allowed' : 'pointer',
                        position: 'relative',
                        zIndex: 2
                      }}
                      onClick={() => !isLocked || isCurrentUserEditing ? handleCellClick(engagement.id, date) : null}
                    >
                      {isEditing ? (
                        <input
                          type="text"
                          value={editValue}
                          onChange={handleInputChange}
                          onBlur={saveEdit}
                          onKeyDown={handleInputKeyPress}
                          autoFocus
                          style={{ width: '100%' }}
                        />
                      ) : (
                        <div>{cellValue}</div>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default TimeSheet;